import * as React from "react"
import {FormattedMessage} from "react-intl"

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faCheckCircle} from "@fortawesome/pro-regular-svg-icons/faCheckCircle"
// import {faInbox} from "@fortawesome/pro-regular-svg-icons/faInbox"

import {useId} from "@reach/auto-id"
import {Dialog} from "@reach/dialog"
import {MenuItem} from "@reach/menu-button"
import {Listbox, ListboxOption} from "@reach/listbox"

import NotificationsContext from "../../../contexts/notifications.js"

import useFetcher from "../../../hooks/fetcher.js"

import {
  Column as DialogColumn,
  Content as DialogContent,
} from "../../../components/dialog.js"
import Row from "../../../components/row.js"
import Loader from "../../../components/loader.js"
import View from "../../../components/view.js"
import {
  StyledButton,
  // StyledButtonSecondary,
  StyledInput,
  StyledLabel,
  StyledTextarea,
} from "../../../components/styled.js"

const SendMessage = props => {
  const {
    children,
    id,
    clientID,
    email,
    phone,
    cannedMessages,
    guestLang,
    siteLang,
  } = props
  const {addError, addSuccess} = React.useContext(NotificationsContext)

  const [showDialog, setShowDialog] = React.useState(false)
  const open = () => setShowDialog(true)
  const close = () => setShowDialog(false)

  const [emailValue, setEmailValue] = React.useState(email)
  const [phoneValue, setPhoneValue] = React.useState(phone)
  const [selectedChannel, setSelectedChannel] = React.useState("mobile")
  const [selectedMessage, setSelectedMessage] = React.useState("none")
  const [messageLocale, setMessageLocale] = React.useState(guestLang)
  const [messageText, setMessageText] = React.useState("")
  const [sendMessageURL, setSendMessageURL] = React.useState(null)
  const sendMessageData = useFetcher(sendMessageURL, {nocache: true})

  const reachID = `send-message--${useId(id)}`

  /**
   * font-size: 13, line-height: 1.2, padding: top 12 & bottom 12
   * initial height : 13 * 1.2 + 12 + 12
   * redraw :
   *  - get line count : current height - padding (top + bottom)
   *  - that gives us one minus line (because scrollHeight takes it into account)
   *  - get height by multiplying the previous number by line height (13 * 1.2)
   *  - add back padding (top + bottom) and one extra line: 12 + 12 + 15.6
   **/
  const [height, setHeight] = React.useState(39.6)
  const textAreaRef = React.useRef(null)
  const handleChange = event => {
    setMessageText(event.target.value)
    setHeight(0)
    setHeight(
      39.6 + Math.floor((textAreaRef.current.scrollHeight - 24) / 15.6) * 15.6
    )
  }

  React.useEffect(() => {
    if (sendMessageData?.error) {
      addError(<FormattedMessage id="send-message.notification.error" />)
    }
    if (!sendMessageData?.data?.sent) return
    setTimeout(() => {
      addSuccess(<FormattedMessage id="send-message.notification.success" />)
      close()
      setSendMessageURL(null)
    }, 2000)
    return
  }, [sendMessageData])

  const handleSelectChannel = value => {
    setSelectedChannel(value)
  }

  const handleSelectMessage = value => {
    setSelectedMessage(value)
    if (value === "none") {
      setMessageLocale(guestLang)
      handleChange({
        target: {value: ""},
      })
      return
    }
    let computedMessageLocale = messageLocale
    if (!cannedMessages?.[value].content.hasOwnProperty(messageLocale)) {
      setMessageLocale(siteLang)
      computedMessageLocale = siteLang
    }
    handleChange({
      target: {value: cannedMessages?.[value].content?.[computedMessageLocale]},
    })
  }

  const handleMessageLocale = value => {
    setMessageLocale(value)
    handleChange({
      target: {value: cannedMessages?.[selectedMessage].content?.[value]},
    })
  }

  const channelValues = {
    mobile: phoneValue,
    email: emailValue,
  }

  const handleSend = () => {
    setSendMessageURL(
      `home/clients/${clientID}/arrivals/sendMessage?id=${id}&msg=${encodeURIComponent(
        messageText
      )}&${selectedChannel}=${encodeURIComponent(
        channelValues[selectedChannel]
      )}`
    )
  }

  return (
    <>
      <MenuItem onSelect={open}>{children}</MenuItem>
      <Dialog isOpen={showDialog} onDismiss={close} aria-labelledby={reachID}>
        <DialogContent
          title={<FormattedMessage id="send-message.title" />}
          close={close}
          buttons={
            sendMessageData?.fetched && sendMessageData?.data?.sent ? (
              <>
                <FormattedMessage id="send-message.message-sent" />
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  size="2x"
                  color="#2abb9c"
                  style={{marginLeft: "12px"}}
                />
              </>
            ) : (
              <>
                {/* <StyledButtonSecondary
                  onClick={() => window.goTo("/home/messagecenter/")}
                >
                  <FontAwesomeIcon
                    icon={faInbox}
                    style={{marginRight: "12px"}}
                  />
                  <FormattedMessage id="send-message.messaging" />
                </StyledButtonSecondary> */}

                <StyledButton ml={4} onClick={handleSend}>
                  {sendMessageData?.loading ? (
                    <Loader size="2x" style={{verticalAlign: "middle"}} />
                  ) : (
                    <FormattedMessage id="send-message.send" />
                  )}
                </StyledButton>
              </>
            )
          }
        >
          <Row>
            <View pr="65px" width="50%">
              <DialogColumn
                label={
                  <FormattedMessage
                    id={`send-message.customer-${selectedChannel}`}
                  />
                }
              >
                <Listbox
                  value={selectedChannel}
                  onChange={handleSelectChannel}
                  style={{minWidth: "70px", marginRight: "5px"}}
                >
                  <ListboxOption value="mobile">
                    <FormattedMessage id="send-message.channel.sms" />
                  </ListboxOption>
                  <ListboxOption value="email">
                    <FormattedMessage id="send-message.channel.email" />
                  </ListboxOption>
                </Listbox>
                {selectedChannel === "mobile" && (
                  <StyledInput
                    px={2}
                    defaultValue={phoneValue}
                    onChange={event => setPhoneValue(event.target.value)}
                  />
                )}
                {selectedChannel === "email" && (
                  <StyledInput
                    px={2}
                    defaultValue={emailValue}
                    onChange={event => setEmailValue(event.target.value)}
                  />
                )}
              </DialogColumn>
            </View>
            <View width="50%">
              <DialogColumn
                label={<FormattedMessage id="send-message.canned-message" />}
              >
                <Listbox
                  value={selectedMessage}
                  onChange={handleSelectMessage}
                  style={{maxWidth: "100%", marginRight: "5px"}}
                >
                  <ListboxOption key={`send-message-${id}-none`} value="none">
                    <FormattedMessage id="send-message.option-none" />
                  </ListboxOption>
                  {Object.entries(cannedMessages).map(
                    ([messageID, message]) => (
                      <ListboxOption key={messageID} value={messageID}>
                        {message.name}
                      </ListboxOption>
                    )
                  )}
                </Listbox>
                {selectedMessage !== "none" && (
                  <Listbox value={messageLocale} onChange={handleMessageLocale}>
                    {Object.entries(
                      cannedMessages[selectedMessage].content
                    ).map(([locale]) => (
                      <ListboxOption
                        key={`${selectedMessage}-${locale}`}
                        value={locale}
                      >
                        <FormattedMessage id={`send-message.lang.${locale}`} />
                      </ListboxOption>
                    ))}
                  </Listbox>
                )}
              </DialogColumn>
            </View>
          </Row>
          <Row mt={4}>
            <StyledLabel>
              <FormattedMessage id="send-message.message" />
            </StyledLabel>
          </Row>
          <Row mt={2}>
            <StyledTextarea
              ref={textAreaRef}
              height={height + "px"}
              onChange={handleChange}
              value={messageText}
            />
          </Row>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SendMessage
